import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLazyGetIconsQuery } from 'redux/services/iconsServices';
import { agencyState } from 'redux/slices/agencysSlice';
import { iconsState, updateIcons, poiCategoryIconsState } from 'redux/slices/iconsSlice';
import { ReactComponent as Ic_Euro } from 'resources/icons/Ic_euro.svg';
import {
  flyTo,
  mapMarKerSelectedState,
  mapMarKerSelectedTypeState,
  mapState,
  mapZoomState,
  updateDrawMarker,
  updateMarkerSelected,
  updateMarkerSelectedType,
  updateMarkers,
  updateMarkerGroup,
  updatePolylines,
} from 'redux/slices/mapSlice';
import { selectedNominatimPoiState } from 'redux/slices/nominatimSlice';
import { stopsState } from 'redux/slices/stopsSlice';
import { topSearchBarState } from 'redux/slices/topSearchBarSlice';
import UseSEO from 'hooks/useSEO/UseSEO';
import MapLeafletContainer from 'components/commons/maps/MapLeafletContainer';
import { transportModeState } from 'redux/slices/transportmodeSlice';
import { IBounds, IMarker } from 'types/interfaces';
import { currentIncludesPath, filterBounds, filterByTransportsMode } from 'utils/utils';
import { categoryState } from 'redux/slices/categorySlice';
import { poisFavoritesState, stopsFavoritesState, directionsFavoritesState } from 'redux/slices/favoritesSlice';
import { poisState } from 'redux/slices/poisSlices';
import {
  RUTAS_GENERALES_MUL2,
  TYPES_MARKERS,
  zoomAcercarseAMarcador,
  zoomMinimoAparicionMarkers,
  zoomMinimoAparicionStop,
} from 'utils/constants';
import { contentRenderMarker, generateClusterMarker } from 'utils/mapUtils';
import './mapScreen.scss';
import { useLanguage } from 'context/languageContext';

export default function MapScreen(props) {
  const { precarga, setInstanceMap } = props;
  const [generateUrl] = UseSEO();
  const location = useLocation();
  const currentLanguage = useLanguage();
  const currentLocation = location.pathname;
  const selectorMap = useSelector(mapState);
  const selectorStops = useSelector(stopsState);
  const markerSelected = useSelector(mapMarKerSelectedState);
  const markerSelectedType = useSelector(mapMarKerSelectedTypeState);
  const selectorSelectedNominatimPoi = useSelector(selectedNominatimPoiState);
  const selectorPois = useSelector(poisState);
  const selectorCategory = useSelector(categoryState);
  const selectorTopSearchBar = useSelector(topSearchBarState);
  const selectorAgencys = useSelector(agencyState);
  const selectorIcons = useSelector(iconsState);
  const selectorPoiCategoryIcons = useSelector(poiCategoryIconsState);
  const selectorStopsFavorites = useSelector(stopsFavoritesState);
  const selectorPoisFavorites = useSelector(poisFavoritesState);
  const selectorDirectionsFavorites = useSelector(directionsFavoritesState);
  const selectorZoom = useSelector(mapZoomState);
  const selectorTransportModes = useSelector(transportModeState);
  const dispatch = useDispatch();
  const [GetIcons] = useLazyGetIconsQuery();
  const navigate = useNavigate();

  // Función para obtener marcadores
  const getMarkers = useCallback(
    async (bounds?: IBounds, zoom?: number) => {
      if (!selectorStops || !selectorPois || !selectorCategory) return;

      let markers: Array<IMarker> = [];
      zoom = zoom || selectorMap.zoom;

      if (zoom < zoomMinimoAparicionMarkers) {
        if (selectorMap.markers.length > 0) {
          dispatch(updateMarkers([]));
          dispatch(updateDrawMarker(''));
        }
        return markers;
      }

      // Filtrar paradas y POIs dentro de los límites del mapa
      let stops = bounds ? filterBounds(selectorStops, bounds).slice(0, 200) : selectorStops;
      let pois = selectorPois.map((item) => ({
        id: item.id,
        name: item.name,
        position: { lat: item.poiLat, lng: item.poiLon },
        category: item.poiCategoryId,
        data: {
          iconPoiCategory: selectorCategory.find((icon) => icon.id === item.poiCategoryId),
          category: item.poiCategoryId,
        },
        type: TYPES_MARKERS.POI,
        topName:
          Number(item?.data?.iconPoiCategory?.section) === 10
            ? '24H'
            : Number(item?.data?.iconPoiCategory?.section) === 40
            ? 'EMT Palma'
            : null,
      }));

      pois = bounds ? filterBounds(pois, bounds) : pois;

      // Filtrar por modos de transporte y categorías
      stops =
        selectorTopSearchBar.filters.transportModes.length > 0 ||
        selectorTopSearchBar.filters.sections?.length > 0
          ? filterByTransportsMode(stops, selectorTopSearchBar.filters.transportModes)
          : stops;

      const poiFilterCat = selectorTopSearchBar.filters.sections.concat(
        selectorTopSearchBar.filters.otherCategories,
      );
      pois =
        poiFilterCat.length > 0 || selectorTopSearchBar.filters.transportModes?.length > 0
          ? pois.filter((poi) => poiFilterCat.includes(parseInt(poi?.data?.iconPoiCategory?.section)))
          : pois;

      // Crear marcadores
      markers = [
        ...pois,
        ...stops.map((item) => ({
          id: item.id,
          name: item.name,
          position: item.position,
          data: {
            iconMarkTransportId: selectorAgencys.dataOrigin.find(
              (itemDataOrigin) => itemDataOrigin.id === item.dataOrigin,
            )?.transportModes[0]?.iconMarkTransportId,
            iconMarkFavTransportId: selectorAgencys.dataOrigin.find(
              (itemDataOrigin) => itemDataOrigin.id === item.dataOrigin,
            )?.transportModes[0].iconMarkFavTransportId,
            stopCode: item.stopCode,
          },
          transportMode: item.transportMode,
          stopCode: item.stopCode,
          type: TYPES_MARKERS.STOP,
        })),
      ];

      if (selectorPois.direction) {
        markers.push(selectorPois.direction);
      }

      // Actualizar marcadores seleccionados
      markers = markers
        .map((marker) => ({
          ...marker,
          iconId: selectorTransportModes?.find(
            (transporte) => transporte.id === Number(marker.transportMode),
          )?.iconBnId,
          selected: marker?.id === selectorMap.markerSelected,
        }))
        .filter((marker) => marker.id);

      // Agrupar marcadores si el zoom es bajo
      if (zoom <= 17) {
        const fn = (group) => {
          dispatch(updateMarkerSelectedType(TYPES_MARKERS.GROUP));
          dispatch(updateMarkerGroup(group));
        };
        markers = generateClusterMarker(markers, 16, Ic_Euro, fn);
      }

      // Obtener íconos faltantes
      const idIcons = markers.reduce((acc: number[], marker) => {
        if (marker.data?.iconMarkTransportId && !acc.includes(marker.data.iconMarkTransportId)) {
          acc.push(marker.data.iconMarkTransportId);
        }
        if (marker.data?.iconPoiCategory?.iconMarkId && !acc.includes(marker.data.iconPoiCategory.iconMarkId)) {
          acc.push(marker.data.iconPoiCategory.iconMarkId);
        }
        if (marker.data?.iconMarkFavTransportId && !acc.includes(marker.data.iconMarkFavTransportId)) {
          acc.push(marker.data.iconMarkFavTransportId);
        }
        if (marker.iconId && !acc.includes(marker.iconId)) {
          acc.push(marker.iconId);
        }
        return acc;
      }, []);

      const idIconsFiltered = idIcons.filter(
        (id) => !selectorIcons.some((icon) => icon.id === id),
      );

      if (idIconsFiltered.length > 0) {
        const resultsPromise = await Promise.all(
          idIconsFiltered.map((id) => GetIcons(id)),
        );
        const newIcons = resultsPromise
          .filter((result) => result.data)
          .map((result) => result.data);
        if (newIcons.length > 0) {
          dispatch(updateIcons(newIcons));
        }
      }

      dispatch(updateMarkers(markers));
      // if (markerSelected) {
      //   dispatch(updateMarkers(markers.filter((m) => m.id === markerSelected)));
      // }

      //NOMINATIM
      if(selectorSelectedNominatimPoi && (markerSelectedType === TYPES_MARKERS.NOMINATIM)) {
        const currentMarkers = markers && markers.length > 0 ? markers : [];
        return dispatch(updateMarkers([...currentMarkers, {...selectorSelectedNominatimPoi, selected:true}]));
      }

      return markers;
    },
    [
      selectorStops,
      selectorPois,
      selectorCategory,
      selectorMap.zoom,
      selectorMap.markers.length,
      selectorTopSearchBar.filters,
      selectorAgencys.dataOrigin,
      selectorTransportModes,
      selectorIcons,
      markerSelected,
      dispatch,
      GetIcons,
    ],
  );

  // Función para renderizar marcadores
  const renderMarkers = useCallback(
    (markers: IMarker[]) => {
      return markers.map((marker) => {
        const result = { ...marker };
        const directios = selectorDirectionsFavorites?.length ? selectorDirectionsFavorites : [];
        result.content = contentRenderMarker(
          marker,
          selectorTransportModes,
          selectorStopsFavorites,
          [...selectorPoisFavorites, ...directios],
          selectorIcons,
          selectorCategory,
          selectorZoom,
          selectorPoiCategoryIcons,
        );
        
        if (
          marker.type === null ||
          marker.type === undefined ||
          marker.type === TYPES_MARKERS.STOP ||
          marker.type === TYPES_MARKERS.POI
        ) {
          if (marker.type === TYPES_MARKERS.STOP) {
            result.adjustIcon = selectorZoom && Number(selectorZoom) > Number(zoomMinimoAparicionStop) ? [30, 30] : [10, 30];
          }
          
          result.onClick = (event: any) => {
            dispatch(
              updateMarkerSelectedType(
                marker.data?.iconPoiCategory || marker.type === TYPES_MARKERS.POI
                  ? TYPES_MARKERS.POI
                  : TYPES_MARKERS.STOP,
              ),
            );
            dispatch(updateMarkerSelected(result.id as number));

            marker.onClick && marker.onClick();
            if (!marker.popUp) {
              navigate(
                generateUrl(RUTAS_GENERALES_MUL2.paradas, TYPES_MARKERS.STOP, {
                  name: marker.name,
                  code: marker.id,
                }),
              );
            }

            if (marker?.position?.lat && marker?.position?.lng) {
              dispatch(
                flyTo({
                  coords: { lat: marker?.position?.lat, lng: marker?.position?.lng },
                  zoom: zoomAcercarseAMarcador,
                }),
              );
            } else {
              console.error('No hay coordenadas disponibles');
            }
          };
        }
        if (marker.type === 'CIRCLESTOP') {
          result.adjustIcon = null; 
        }
        return result;
      });
    },
    [
      selectorTransportModes,
      selectorStopsFavorites,
      selectorPoisFavorites,
      selectorIcons,
      selectorCategory,
      selectorZoom,
      selectorPoiCategoryIcons,
      location,
      dispatch,
      navigate,
      generateUrl,
    ],
  );

  // Efecto para cargar marcadores
  useEffect(() => {
    if (currentIncludesPath(currentLocation, RUTAS_GENERALES_MUL2.paradas[currentLanguage])) {
      dispatch(updateMarkers([]));
      if (precarga && !selectorMap.blockMarkerAction) {
        if (selectorMap.bounds) {
          getMarkers(selectorMap.bounds, selectorMap.zoom);
        }
      }
    }
  }, [
    currentLocation,
    precarga,
    selectorMap.bounds,
    selectorMap.zoom,
    selectorMap.blockMarkerAction,
    getMarkers,
    dispatch,
    currentLanguage,
  ]);

  // Efecto para limpiar polilíneas
  useEffect(() => {
    if (currentLocation.includes(RUTAS_GENERALES_MUL2.paradas[currentLanguage])) {
      dispatch(updatePolylines([]));
    }
  }, [currentLocation, dispatch, currentLanguage]);

  return (
    <MapLeafletContainer
      markers={renderMarkers(selectorMap.markers)}
      setMapInstance={setInstanceMap}
      polylines={selectorMap.polylines}
      genericElements={selectorMap.genericElementsInMap}
    />
  );
}
